import type { ReactNode } from 'react';
import { useApi } from '../../use_api';
import { get_linkedin_connections, type LinkedinConnections } from '../../api';
import { IconExternal, IconCheck, IconLinkedIn, IconWordPress, IconFramer, IconGitHub } from '../../components/icons';

// Publish Connections — a live, read-only status board for everything the Publish app
// publishes through on the tenant's behalf. Connection state is read from the shared platform
// Firestore (the BFF endpoint); the actual connect/disconnect actions are OAuth + form flows
// bound to publish.captomi.com, so each row links out there to manage.

function StatusPill({ on, label }: { on: boolean; label?: string }) {
  return on ? (
    <span className="badge success">
      <IconCheck style={{ width: 13, height: 13 }} /> {label ?? 'Connected'}
    </span>
  ) : (
    <span className="badge">
      <span className="dot" style={{ background: 'var(--text-3)' }} /> Not connected
    </span>
  );
}

function Row({
  title,
  desc,
  detail,
  on,
  onLabel,
  manage_url,
  icon,
}: {
  title: string;
  desc: string;
  detail?: ReactNode;
  on: boolean;
  onLabel?: string;
  manage_url: string;
  icon?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '16px 4px',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {icon && <span style={{ display: 'inline-flex', color: 'var(--text-2)' }}>{icon}</span>}
          <strong style={{ fontSize: 14.5 }}>{title}</strong>
          <StatusPill on={on} label={onLabel} />
        </div>
        <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>
          {detail ?? desc}
        </div>
      </div>
      <a className="btn secondary sm" href={manage_url} target="_blank" rel="noopener noreferrer">
        {on ? 'Manage' : 'Connect'} <IconExternal style={{ width: 13, height: 13 }} />
      </a>
    </div>
  );
}

function provider_label(p?: string): string {
  if (!p) return '';
  return ({ framer: 'Framer', wordpress: 'WordPress', github: 'GitHub', google_docs: 'Google Docs' } as Record<string, string>)[p] ?? p;
}

export function ConnectionsView() {
  const { loading, data, error } = useApi<LinkedinConnections>(get_linkedin_connections, []);
  const manage = data?.manage_url ?? 'https://publish.captomi.com';

  return (
    <div className="page">
      <div className="page-header">
        <h1>Publish</h1>
        <p className="sub">Connect the accounts and channels Captomi publishes to on your behalf.</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, color: 'var(--text-3)' }}>
          <span style={{ fontSize: 12.5 }}>Publishes across</span>
          <IconLinkedIn role="img" aria-label="LinkedIn" style={{ width: 16, height: 16 }} />
          <IconWordPress role="img" aria-label="WordPress" aria-hidden={false} style={{ width: 15, height: 15 }} />
          <IconFramer role="img" aria-label="Framer" aria-hidden={false} style={{ width: 14, height: 14 }} />
          <IconGitHub role="img" aria-label="GitHub" aria-hidden={false} style={{ width: 15, height: 15 }} />
        </div>
      </div>

      {loading && (
        <div className="card">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skeleton" style={{ height: 20, margin: '14px 0' }} />
          ))}
        </div>
      )}
      {error && <div className="card error">Couldn't load connections ({error}).</div>}

      {data && (
        <div className="card" style={{ paddingTop: 6 }}>
          <Row
            icon={<IconLinkedIn style={{ width: 16, height: 16 }} />}
            title="Personal LinkedIn"
            desc="Your LinkedIn account, used to publish posts as you."
            on={data.personal.connected}
            onLabel={data.personal.status === 'expired' ? 'Reconnect needed' : 'Connected'}
            detail={
              data.personal.connected
                ? data.personal.status === 'expired'
                  ? 'Access expired — reconnect to keep publishing.'
                  : 'Publishing as your profile.'
                : 'Connect your account so Captomi can publish posts for you.'
            }
            manage_url={manage}
          />
          <Row
            icon={<IconLinkedIn style={{ width: 16, height: 16 }} />}
            title="Company Page"
            desc="Publish to your organization's LinkedIn page."
            on={data.company_page.connected}
            detail={data.company_page.connected ? data.company_page.name ?? 'Page connected.' : 'Connect a company page to post as your brand.'}
            manage_url={manage}
          />
          <Row
            icon={
              data.blog.connected && data.blog.provider === 'wordpress' ? (
                <IconWordPress style={{ width: 16, height: 16 }} />
              ) : data.blog.connected && data.blog.provider === 'framer' ? (
                <IconFramer style={{ width: 16, height: 16 }} />
              ) : (
                <span style={{ display: 'inline-flex', gap: 4 }}>
                  <IconWordPress style={{ width: 15, height: 15 }} />
                  <IconFramer style={{ width: 15, height: 15 }} />
                </span>
              )
            }
            title="Blog"
            desc="Cross-post articles to your blog."
            on={data.blog.connected}
            detail={
              data.blog.connected
                ? `${provider_label(data.blog.provider)}${data.blog.detail ? ` · ${data.blog.detail}` : ''}`
                : 'Connect Framer or WordPress to publish articles.'
            }
            manage_url={manage}
          />
          <Row
            icon={<IconGitHub style={{ width: 16, height: 16 }} />}
            title="Tracker"
            desc="Track content work in GitHub."
            on={data.tracker.connected}
            detail={data.tracker.connected ? data.tracker.repo ?? provider_label(data.tracker.provider) : 'Link a GitHub repo to track your content pipeline.'}
            manage_url={manage}
          />
          <Row
            title="Draft review"
            desc="Review drafts in Google Docs before publishing."
            on={data.review.enabled}
            onLabel="Enabled"
            detail={data.review.enabled ? `${provider_label(data.review.provider)} review enabled.` : 'Enable Google Docs review for drafts.'}
            manage_url={manage}
          />
        </div>
      )}
    </div>
  );
}
